import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

const REMINDER_NOTIFICATION_ID = 9001;
const SETTINGS_KEY = 'repslearn_reminder_settings';

export interface ReminderSettings {
  enabled: boolean;
  hour: number;   // 0-23
  minute: number;  // 0-59
}

const DEFAULT_SETTINGS: ReminderSettings = {
  enabled: false,
  hour: 17,    // 5:00 PM
  minute: 0,
};

export const isNative = (): boolean => Capacitor.isNativePlatform();

export const getSettings = (): ReminderSettings => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { ...DEFAULT_SETTINGS };
};

export const saveSettings = (settings: ReminderSettings): void => {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
};

export const checkPermission = async (): Promise<boolean> => {
  if (!isNative()) return false;
  const result = await LocalNotifications.checkPermissions();
  return result.display === 'granted';
};

export const requestPermission = async (): Promise<boolean> => {
  if (!isNative()) return false;
  // Check current status first
  const current = await LocalNotifications.checkPermissions();
  if (current.display === 'granted') return true;
  // Request permission — triggers the native Android dialog
  const result = await LocalNotifications.requestPermissions();
  return result.display === 'granted';
};

export const scheduleDailyReminder = async (hour: number, minute: number): Promise<void> => {
  if (!isNative()) return;

  // Cancel any existing reminder first
  await cancelDailyReminder();

  // Schedule at the chosen time every day
  const now = new Date();
  const scheduled = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0);
  // If the time already passed today, schedule for tomorrow
  if (scheduled <= now) {
    scheduled.setDate(scheduled.getDate() + 1);
  }

  await LocalNotifications.schedule({
    notifications: [
      {
        id: REMINDER_NOTIFICATION_ID,
        title: 'Time to practice!',
        body: 'Just 5 questions. Keep your streak going!',
        schedule: {
          at: scheduled,
          repeats: true,
          every: 'day',
        },
        actionTypeId: 'QUICK_PRACTICE',
        extra: { route: '/practice/0/0?mode=quick' },
      },
    ],
  });
};

export const cancelDailyReminder = async (): Promise<void> => {
  if (!isNative()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: REMINDER_NOTIFICATION_ID }] });
  } catch { /* may not exist yet */ }
};

export const initNotificationListeners = (navigate: (path: string) => void): void => {
  if (!isNative()) return;

  // Show notifications even when app is in the foreground
  LocalNotifications.addListener('localNotificationReceived', (_notification) => {
    // Notification is automatically displayed by the system
  });

  LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
    const route = action.notification.extra?.route;
    if (route) {
      navigate(route);
    }
  });
};
